// src/services/donorService.ts
import axios, { AxiosHeaders, AxiosRequestHeaders } from "axios";
import { API_BASE_URL } from "../config";
import { getAuthToken, clearAuthToken, getIdentityFromToken } from "./authService";
import { asErr } from "../utils/apiHelpers";
import type { DonorRow } from "../types/donor";
/** OTP identifier kinds supported by the modal. */
export type OtpIdentifierKind = "phone" | "email" | "nationalId";

// Use relative URLs in development (proxy) or absolute in production
const getBaseURL = () => {
  if (process.env.NODE_ENV === "development") return "";
  return API_BASE_URL;
};

// Central axios instance for donor endpoints
const http = axios.create({
  baseURL: getBaseURL(),
  headers: { Accept: "application/json" },
  validateStatus: (s) => s >= 200 && s < 300,
  timeout: 60000,
});

// 🔥 One-time logout lock here too (because this file calls check/user directly)
let logoutFired = false;
function fireLogoutOnce(reason?: string) {
  if (logoutFired) return;
  logoutFired = true;

  clearAuthToken();
  window.dispatchEvent(new CustomEvent("auth:logout", { detail: { reason } }));
}

// Public endpoints: do NOT send Authorization (avoids AUTH_401 when user has expired token during registration)
const PUBLIC_DONOR_PATHS = ["/api/auth/otp/start"];
const isPublicDonorEndpoint = (url?: string) =>
  url && PUBLIC_DONOR_PATHS.some((p) => url.includes(p));

/* Inject Authorization automatically from stored JWT (skip for public endpoints) */
http.interceptors.request.use((config) => {
  const tok = getAuthToken();

  // Debug log: this is the MOST important thing to see
  console.log("[donorService REQ]", config.method?.toUpperCase(), config.url, {
    tokLen: tok?.length ?? 0,
    tokenKey: "authToken",
  });

  // Skip Authorization for public registration endpoints (prevents AUTH_401 from expired token)
  if (tok && !isPublicDonorEndpoint(config.url)) {
    const h = (config.headers ?? {}) as AxiosRequestHeaders | AxiosHeaders;
    (h as any).Authorization = `Bearer ${tok}`;
    config.headers = h as any;
  }

  return config;
});

/* Normalize 401 and stabilize auth (stop flicker) */
http.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url as string | undefined;

    console.log("[donorService ERR]", status, url);

    if (status === 401) {
      // ✅ If check/user is 401 => user is not logged in.
      // Clear token ONCE so React stops thinking user is authenticated.
      if (url?.includes("/api/donation/check/user") || url?.includes("/api/donation/")) {
        fireLogoutOnce(`401 from ${url ?? "unknown"}`);
      }

      const e = new Error("AUTH_401");
      (e as any).code = "AUTH_401";
      throw e;
    }

    throw error;
  }
);

/* ---------- Existing donor create DTOs (unchanged) ---------- */
export type DonorListItem = {
  id?: number;
  name?: string;
  nationalId?: string;
  resPartnerId?: number;
  phone?: string;
  email?: string;
  donorType?: string;
  nationality?: string;
  nationalityName?: string;
  countryName?: string;
  occupation?: string;
  relation?: string;
  dob?: string;
  dobGregorian?: string;
  dobHijri?: string;
  expiryDate?: string;
  creationPlatform?: string;
};

export type CreateDonorRequest = {
  name?: string | null;
  nationalId: string;
  DonorType: "indirect" | "direct";
  phone?: string | null;
  email?: string | null;
  countryName?: string | null;
  relation?: string | null;
  occupation?: string | null;
  dob?: string | null; // yyyy-MM-dd
  dobHijri?: string | null;
  expiry_date?: string | null;
  creation_platform?: string | null;
};

export type CreateDonorResponse = { id: number } & Record<string, any>;

export type CurrentUserDonorProfile = {
  name?: string;
  nationalId?: string;
  phone?: string;
  email?: string;
  donorType?: string;
  countryName?: string;
  dobGregorian?: string;
  dobHijri?: string;
};

type DonorIdentityLike = {
  nationalId?: string;
  phone?: string;
  donorType?: string;
};

/** Normalize phone to digits only for comparison. */
function phoneDigits(s: string | null | undefined): string {
  return (s ?? "").toString().replace(/\D/g, "").trim();
}

function normalizeDonorType(value: unknown): string {
  return (value ?? "").toString().trim().toLowerCase();
}

function isIndirectDonor(value: unknown): boolean {
  const donorType = normalizeDonorType(value);
  return donorType === "indirect" || donorType === "غير مباشر";
}

function findIdentityMatch<T extends DonorIdentityLike>(donors: T[]): T | null {
  const identity = getIdentityFromToken();
  if (!identity?.nationalId && !identity?.phone) return null;

  const idNorm = (identity.nationalId ?? "").toString().trim();
  const phoneNorm = phoneDigits(identity.phone);
  return (
    donors.find((d) => {
      if (idNorm && (d.nationalId ?? "").toString().trim() === idNorm) return true;
      if (phoneNorm && phoneDigits(d.phone) === phoneNorm) return true;
      return false;
    }) ?? null
  );
}

function normalizeDateString(value: unknown): string {
  return (value ?? "").toString().trim();
}

function yearFromDateString(value: string): number | null {
  const match = value.match(/^(\d{4})/);
  if (!match) return null;
  const year = Number(match[1]);
  return Number.isFinite(year) ? year : null;
}

function looksLikeHijriDate(value: string): boolean {
  const year = yearFromDateString(value);
  return year !== null && year >= 1200 && year <= 1700;
}

function looksLikeGregorianDate(value: string): boolean {
  const year = yearFromDateString(value);
  return year !== null && year >= 1700 && year <= 2500;
}

/**
 * Resolve the donor that belongs to the current authenticated account owner.
 * Priority:
 * - exact JWT identity match,
 * - the only donor on the account,
 * - the only non-indirect donor when indirect delegates were added.
 * Never fall back to the first donor when multiple rows are ambiguous.
 */
export function pickDonorForCurrentUser<T extends DonorIdentityLike>(donors: T[]): T | null {
  if (!Array.isArray(donors) || donors.length === 0) return null;
  const exactMatch = findIdentityMatch(donors);
  if (exactMatch) return exactMatch;
  if (donors.length === 1) return donors[0] ?? null;

  const nonIndirectDonors = donors.filter((d) => !isIndirectDonor(d.donorType));
  if (nonIndirectDonors.length === 1) return nonIndirectDonors[0] ?? null;

  return null;
}

/**
 * Pick the authenticated donor using server data only.
 * - If there is exactly one donor, accept it.
 * - If there are multiple donors, require an exact JWT identity match.
 * - Never fall back to donors[0] when multiple rows exist.
 */
function pickVerifiedDonorForCurrentUser(donors: DonorListItem[]): DonorListItem | null {
  return pickDonorForCurrentUser(donors);
}

/**
 * Returns the single donor that should be visible outside the benefactors page.
 * Indirect delegates stay available in the benefactors list only.
 */
export function scopeDonorsToCurrentUser<T extends DonorIdentityLike>(donors: T[]): T[] {
  const donor = pickDonorForCurrentUser(donors);
  return donor ? [donor] : [];
}

function toCurrentUserDonorProfile(donor: DonorListItem | null): CurrentUserDonorProfile | null {
  if (!donor) return null;
  const d = donor as Record<string, unknown>;
  const dobCandidates = [
    normalizeDateString(d.dobHijri),
    normalizeDateString(d.dobGregorian),
    normalizeDateString(donor.dob),
  ].filter(Boolean);

  const dobHijri =
    dobCandidates.find((value) => looksLikeHijriDate(value)) ||
    normalizeDateString(d.dobHijri) ||
    undefined;
  const dobGregorian =
    dobCandidates.find((value) => looksLikeGregorianDate(value)) ||
    normalizeDateString(d.dobGregorian) ||
    normalizeDateString(donor.dob) ||
    undefined;

  return {
    name: (donor.name ?? "").toString() || undefined,
    nationalId: (donor.nationalId ?? "").toString() || undefined,
    phone: (donor.phone ?? "").toString() || undefined,
    email: (donor.email ?? "").toString() || undefined,
    donorType: (donor.donorType ?? "").toString() || undefined,
    countryName: (d.nationalityName ?? d.nationality ?? d.countryName ?? "").toString() || undefined,
    dobGregorian,
    dobHijri,
  };
}

export async function getCurrentUserDonorProfile(): Promise<CurrentUserDonorProfile | null> {
  const donors = await listCurrentUserDonors();
  return toCurrentUserDonorProfile(pickVerifiedDonorForCurrentUser(donors));
}

/**
 * Legacy helper returning a loose list item shape.
 * Left intact in case other screens still import it.
 */
export async function listCurrentUserDonors(): Promise<DonorListItem[]> {
  try {
    const { data } = await http.get("/api/donation/check/user", {
      responseType: "text",
      transformResponse: [(d) => d],
    });
    const payload = JSON.parse(data as string);
    return Array.isArray(payload) ? (payload as DonorListItem[]) : [];
  } catch (e) {
    if ((e as any)?.message === "AUTH_401") throw e;
    throw asErr("listCurrentUserDonors failed", e);
  }
}

const CREATE_DONOR_RETRY_DELAY_MS = 1500;
const CREATE_DONOR_MAX_RETRIES = 2;

export async function createDonor(payload: CreateDonorRequest): Promise<CreateDonorResponse> {
  const body = {
    name: payload.name,
    nationalId: payload.nationalId,
    donorType: payload.DonorType,
    phone: payload.phone,
    email: payload.email,
    countryName: payload.countryName,
    relation: payload.relation,
    occupation: payload.occupation,
    dob: payload.dob,
    dobHijri: payload.dobHijri,
    expiryDate: payload.expiry_date,
    creationPlatform: payload.creation_platform,
  };

  let lastError: unknown = null;
  for (let attempt = 0; attempt <= CREATE_DONOR_MAX_RETRIES; attempt++) {
    try {
      const { data } = await http.post("/api/donation/donor/create", body, {
        headers: { "Content-Type": "application/json" },
      });
      return data as CreateDonorResponse;
    } catch (e) {
      lastError = e;
      if ((e as any)?.message === "AUTH_401") throw e;
      const status = (e as any)?.response?.status;
      if (status === 502 && attempt < CREATE_DONOR_MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, CREATE_DONOR_RETRY_DELAY_MS));
        continue;
      }
      throw asErr("createDonor failed", e);
    }
  }
  throw asErr("createDonor failed", lastError);
}

/* ---------- OTP endpoints (unchanged) ---------- */
export async function startDonorPhoneOtp(
  phone: string,
  captchaToken: string | null,
  cont?: string
): Promise<{ ok: boolean; cooldownSeconds?: number; message?: string }> {
  try {
    const { data } = await http.post(
      "/api/auth/otp/start",
      { phone, captchaToken, continue: cont ?? null },
      {
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        timeout: 90000, // ✅ 90 seconds (adjust as needed)
        validateStatus: (s) => s >= 200 && s < 500,
      }
    );
    return { ok: true, cooldownSeconds: Number(data?.cooldownSeconds) || undefined };
  } catch (e: any) {
    if (axios.isAxiosError(e) && (e.code === "ECONNABORTED" || /timeout/i.test(e.message))) {
      return { ok: false, message: "REQUEST_TIMEOUT" };
    }
    if ((e as any)?.message === "AUTH_401") return { ok: false, message: "AUTH_401" };
    const msg = asErr("startDonorPhoneOtp failed", e).message;
    return { ok: false, message: msg };
  }
}

export async function verifyDonorPhoneOtp(
  phone: string,
  code: string
): Promise<{ ok: boolean; message?: string }> {
  try {
    await http.post("/api/auth/otp/verify", { phone, code }, { headers: { "Content-Type": "application/json" } });
    return { ok: true };
  } catch (e) {
    if ((e as any)?.message === "AUTH_401") return { ok: false, message: "AUTH_401" };
    const s = asErr("verifyDonorPhoneOtp failed", e).message;
    return { ok: false, message: s };
  }
}


/**
 * Verify OTP sent to email.
 * Keeps old phone verification untouched and adds email verification support.
 *
 * Backend note:
 * - This matches the identifier-based verify contract:
 *   POST /api/auth/otp/verify { identifier, code }
 * - If your backend expects a different field name, adjust only this function.
 */
export async function verifyDonorEmailOtp(
  email: string,
  code: string
): Promise<{ ok: boolean; message?: string }> {
  try {
    await http.post(
      "/api/auth/otp/verify",
      { identifier: (email || "").trim(), code },
      { headers: { "Content-Type": "application/json" } }
    );
    return { ok: true };
  } catch (e) {
    if ((e as any)?.message === "AUTH_401") return { ok: false, message: "AUTH_401" };
    const s = asErr("verifyDonorEmailOtp failed", e).message;
    return { ok: false, message: s };
  }
}

/**
 * Generic OTP start wrapper used by identifier-based UI.
 *
 * IMPORTANT:
 * - Does NOT change existing working phone/email functions.
 * - Reuses current implementations internally.
 * - nationalId is intentionally blocked here until backend support is confirmed.
 */
export async function startDonorOtp(
  identifierKind: OtpIdentifierKind,
  identifierValue: string,
  captchaToken: string | null,
  cont?: string
): Promise<{ ok: boolean; cooldownSeconds?: number; message?: string }> {
  const value = (identifierValue || "").trim();

  switch (identifierKind) {
    case "phone":
      return startDonorPhoneOtp(value, captchaToken, cont);

    case "email":
      return startDonorEmailOtp(value, captchaToken, cont);

    case "nationalId":
      // Do not fake support. Keep explicit until backend/API contract is ready.
      return { ok: false, message: "NATIONAL_ID_OTP_NOT_SUPPORTED" };

    default:
      return { ok: false, message: "INVALID_IDENTIFIER_KIND" };
  }
}

/**
 * Generic OTP verify wrapper used by identifier-based UI.
 *
 * IMPORTANT:
 * - Reuses current specific verification methods.
 * - nationalId remains unsupported until backend confirms contract.
 */
export async function verifyDonorOtp(
  identifierKind: OtpIdentifierKind,
  identifierValue: string,
  code: string
): Promise<{ ok: boolean; message?: string }> {
  const value = (identifierValue || "").trim();

  switch (identifierKind) {
    case "phone":
      return verifyDonorPhoneOtp(value, code);

    case "email":
      return verifyDonorEmailOtp(value, code);

    case "nationalId":
      return { ok: false, message: "NATIONAL_ID_OTP_NOT_SUPPORTED" };

    default:
      return { ok: false, message: "INVALID_IDENTIFIER_KIND" };
  }
}
/* ---------- OTP via email (for benefactor signup) — يطابق POST /api/auth/otp/start: identifier, phone, captchaToken, continue ---------- */
export async function startDonorEmailOtp(
  email: string,
  captchaToken: string | null,
  cont?: string,
  phone?: string
): Promise<{ ok: boolean; cooldownSeconds?: number; message?: string }> {
  try {
    const body: Record<string, string> = {
      identifier: (email || "").trim(),
      phone: (phone || "").trim(),
      captchaToken: (captchaToken ?? "").trim(),
      continue: (cont ?? "").trim(),
    };
    const { data } = await http.post(
      "/api/auth/otp/start",
      body,
      {
        headers: { "Content-Type": "application/json" },
        timeout: 45000,
      }
    );
    return { ok: true, cooldownSeconds: Number(data?.cooldownSeconds) || undefined };
  } catch (e: any) {
    if (axios.isAxiosError(e) && (e.code === "ECONNABORTED" || /timeout/i.test(e.message))) {
      return { ok: false, message: "REQUEST_TIMEOUT" };
    }
    if ((e as any)?.message === "AUTH_401") return { ok: false, message: "AUTH_401" };
    const msg = asErr("startDonorEmailOtp failed", e).message;
    return { ok: false, message: msg };
  }
}

/* ---------- New: strongly-typed donor list for current user ---------- */
export async function listMine(): Promise<DonorRow[]> {
  try {
    const { data } = await http.get("/api/donation/check/user", {
      responseType: "text",
      transformResponse: [(d) => d],
    });
    const payload = JSON.parse(data as string);
    if (!Array.isArray(payload)) throw new Error("Unexpected donors payload shape.");
    return payload as DonorRow[];
  } catch (e) {
    if ((e as any)?.message === "AUTH_401") throw e;
    throw asErr("listMine failed", e);
  }
}
